import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './layouts/HomePage';
import AboutPage from './layouts/AboutPage';
import SearchPage from './layouts/SearchPage';
import OrganisationPage from './layouts/OrganisationPage';
import { FiltersProvider } from './context/FiltersContext';
import AskQuestionPage from './layouts/AskQuestionPage';
import ContactPage from './layouts/ContactPage';
import SyncPage from './layouts/AdminSyncPage';
import ScrollToTop from './components/ScrollToTop';
import SeoAdminPage from './layouts/AdminSEOPage';
import AdminLoginPage from './layouts/AdminLoginPage';
import AdminDashboardPage from './layouts/AdminDashboardPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminUsersPage from './layouts/AdminUsersPage';
import AdminRolesPage from './layouts/AdminRolesPage';
import UserPage from './layouts/UserPage';
import LawyerProfilePage from './layouts/LawyerProfilePage';
import UserSignupPage from './layouts/UserSignupPage';
import SignInPage from './layouts/SignInPage';
import ForgotPasswordPage from './layouts/ForgotPasswordPage';
import ResetPasswordPage from './layouts/ResetPasswordPage';
import PrivacyPolicyPage from './layouts/PrivacyPolicyPage';
import TermsOfServicePage from './layouts/TermsOfServicePage';
import CookiePolicyPage from './layouts/CookiePolicyPage';
import CookieSettingsPage from './layouts/CookieSettingsPage';
import NotFoundPage from './layouts/NotFoundPage';
import { useSync } from './hooks/useSync';
import { useLayoutVisibility } from './hooks/useLayoutVisibility';
import { Toaster } from 'react-hot-toast';
import './styles/global.css';
import LegalProfessionalsPage from './layouts/LegalProfessionalsPage';
import ManagersPage from './layouts/ManagersPage';
import LegalProfessionalSignupPage from './layouts/LegalProfessionalSignupPage';
import { VerifyEmail } from './components/user/VerifyEmail';
import GoogleCallback from './components/GoogleCallback';
import LegalServicesPage from './components/LegalServicesPage';
import CookieConsent from './components/common/CookieConsent';
import UserProfilePage from './layouts/UserProfilePage';
import LegalProfessionalProfilePage from './layouts/LegalProfessionalProfilePage';
import ProfileRouter from './components/ProfileRouter';
import ClaimEnquiryPage from './layouts/ClaimEnquiryPage';
import ClaimAppointmentPage from './layouts/ClaimAppointmentPage';
import ClaimSuccessDetails from './components/claim/ClaimSuccessDetails';
import { SearchStateProvider } from './contexts/SearchStateContext';
import AdminAppointmentsPage from './layouts/AdminAppointmentsPage';

// Add styles to remove padding when header and footer are hidden
const styles = `
  .page__main--no-header-footer {
    padding: 0 !important;
    margin: 0 !important;
  }
  
  body, html {
    margin: 0;
    padding: 0;
  }

  /* Only apply overflow hidden to specific pages that need it */
  body.no-scroll, html.no-scroll {
    overflow: hidden;
  }

  /* Allow scrolling on signup page */
  body.allow-scroll, html.allow-scroll {
    overflow: auto !important;
    -webkit-overflow-scrolling: touch;
    height: auto !important;
  }
  
  /* Loading spinner */
  .loading-spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100%;
  }
  
  .loading-spinner:after {
    content: "";
    width: 50px;
    height: 50px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid #D80364;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Wrapper component for auth validation
const AppContent = () => {
  const { user, loading, validateToken } = useAuth();
  const navigate = useNavigate();
  const showHeaderFooter = useLayoutVisibility();
  const { isSyncing, error, handleSync } = useSync();
  const [apiError, setApiError] = useState(null);
  // Move the ref to component top level
  const lastValidationRef = React.useRef(0);

  // Set up global error handling for API issues
  useEffect(() => {
    // Global handler for unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      console.error('Unhandled Promise Rejection:', error);
      
      // Check if it's a common API resource error
      if (error?.message?.includes('ERR_INSUFFICIENT_RESOURCES') || 
          error?.name === 'AbortError' ||
          error?.code === 'ECONNABORTED') {
        setApiError('Server is experiencing high load. Please refresh the page or try again later.');
        
        // Automatically clear the error after 5 seconds
        setTimeout(() => setApiError(null), 5000);
      }
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Check token validity on page load and when the app is focused
  useEffect(() => {
    // Define the constant here instead of using useRef
    const MIN_VALIDATION_INTERVAL = 5000; // 5 seconds minimum between validations
    
    const checkTokenValidity = async () => {
      try {
        // Check if on homepage - don't force validation that might redirect
        const isHomePage = window.location.pathname === '/';
        if (isHomePage) {
          console.log('Skipping token validation on homepage');
          return;
        }
        
        // Check if enough time has passed since last validation
        const now = Date.now();
        if (now - lastValidationRef.current < MIN_VALIDATION_INTERVAL) {
          console.log('Skipping token validation, too soon since last check');
          return;
        }
        
        // Update last validation time
        lastValidationRef.current = now;
        
        await validateToken();
      } catch (error) {
        // If validation fails, the user will be redirected to login by the interceptor in AuthContext
        console.error('Token validation failed:', error);
      }
    };

    // Initial check
    checkTokenValidity();

    // Re-validate when the window gets focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkTokenValidity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [validateToken]);

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <>
      <style>{styles}</style>
      <ScrollToTop />
      {showHeaderFooter && <Header onSync={handleSync} syncing={isSyncing} />}
      <main className={`page__main ${!showHeaderFooter ? 'page__main--no-header-footer' : ''}`}>
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button 
              className="error-message__dismiss"
              onClick={() => window.location.reload()}
            >
              ×
            </button>
          </div>
        )}
        
        {apiError && (
          <div className="api-error-message bg-amber-50 border-l-4 border-amber-500 p-4 fixed top-20 right-4 z-50 shadow-md rounded-md max-w-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-amber-700">{apiError}</p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button 
                    onClick={() => setApiError(null)}
                    className="inline-flex rounded-md p-1.5 text-amber-500 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <Routes>
          {/* Specific routes first - order matters! */}
          <Route path="/" element={<HomePage />} />
          <Route 
            path="/search" 
            element={<UserPage />} 
          />
          <Route path="/about" element={<AboutPage />} />
          {/* <Route path="/search" element={<SearchPage />} /> */}
          <Route path="/solicitors/:slug" element={<OrganisationPage />} />
          <Route path="/sync" element={<SyncPage />} />
          <Route path="/ask-a-question" element={<AskQuestionPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/cookie-settings" element={<CookieSettingsPage />} />
          {/* <Route path="/" element={<UserPage />} /> */}
          
          {/* SEO-friendly URL patterns for LawyerProfilePage - order matters! */}
          {/* Most specific route (with category, city, and solicitor name) */}
          <Route path="/:category/solicitor/:city/:solicitorName" element={<LawyerProfilePage />} />
          
          {/* Route with city and solicitor name */}
          <Route path="/solicitor/:city/:solicitorName" element={<LawyerProfilePage />} />
          
          {/* Route with just solicitor name or ID */}
          <Route path="/solicitor/:solicitorName" element={<LawyerProfilePage />} />

          <Route path="/signup" element={<UserSignupPage />} />
          <Route path="/signup/legal-professional" element={<LegalProfessionalSignupPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/google-callback" element={<GoogleCallback />} />
          <Route path="/legal-services" element={<LegalServicesPage />} />
          <Route path="/claim-enquiry/:token" element={<ClaimEnquiryPage />} />
          <Route path="/claim-appointment/:token" element={<ClaimAppointmentPage />} />
          
          {/* Legal Professional Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireRole="legal_professional">
                <ProfileRouter />
                <ClaimSuccessDetails />
              </ProtectedRoute>
            }
          />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireRole="manager">
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seo"
            element={
              <ProtectedRoute requireRole="manager">
                <SeoAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sra-sync"
            element={
              <ProtectedRoute requireRole="manager">
                <SyncPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute requireRole="admin">
                <AdminRolesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/legal-professionals"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <LegalProfessionalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/managers"
            element={
              <ProtectedRoute requireRole="admin">
                <ManagersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/appointments"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <AdminAppointmentsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFoundPage />} />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <ProfileRouter />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </main>
      {showHeaderFooter && <Footer />}
      <CookieConsent />
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <FiltersProvider>
        <SearchStateProvider>
        <AppContent />
        </SearchStateProvider>
      </FiltersProvider>
    </AuthProvider>
  );
}

export default App;
